import { forkJoin, map, ReplaySubject, Subject } from 'rxjs';
import { take, filter, switchMap, debounceTime, takeUntil } from 'rxjs/operators';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CachedCurrentUserService } from '@pure1/data';

import { getEmailDomain } from '../utils/strings';
import { WINDOW } from './injection-tokens';
import { AuthenticationService } from '../services/authentication.service';
import { Angulartics2AmplitudePSTG } from './angulartics-amplitude-pstg.service';
import { isPure1Production } from '../utils/url';
import { ampli, ApiKey, IdentifyProperties } from '../ampli';
import { ApplicationAssignmentsService } from '../data/services/application-assignments.service';
import { EffectiveOrg, EffectiveOrgService } from '../data/services/effective-org.service';
import { AmplitudeBufferService } from './amplitude-buffer.service';
import { AmplitudeSessionReplayService } from './amplitude-session-replay.service';
import { migrateLegacyAmplitudeCookies } from './amplitude-legacy-cookies';
import { removeOrgUidFromPath } from '../data/utils/org-url';

@Injectable({ providedIn: 'root' })
export class AngularticsConfigService implements OnDestroy {
    readonly userIdentified$ = new ReplaySubject<{
        originalEmailDomain: string;
        impersonatedEmailDomain: string;
        originalContactId: string;
        impersonatedContactId: string;
    }>(1);
    private readonly pageTrack$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        private readonly cachedCurrentUserService: CachedCurrentUserService,
        private readonly angulartics2AmplitudePSTG: Angulartics2AmplitudePSTG,
        private readonly authenticationService: AuthenticationService,
        private readonly applicationAssignmentsService: ApplicationAssignmentsService,
        private readonly effectiveOrgService: EffectiveOrgService,
        private readonly router: Router,
        private readonly amplitudeBuffer: AmplitudeBufferService,
        private readonly amplitudeSessionReplay: AmplitudeSessionReplayService,
        private readonly ngZone: NgZone,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    /**
     * Hooks up Angulartics2 for Amplitude.
     */
    run(): void {
        // Load the SDK (and Session Replay, which hangs off the same promise) outside the Angular
        // zone: their timers, fetches and, while a session is being recorded, rrweb's document-wide
        // mousemove/scroll/input listeners would otherwise each run a full change detection.
        this.ngZone
            .runOutsideAngular(() => this.initAmplitude(isPure1Production(this.window.location)))
            .then(() => {
                this.angulartics2AmplitudePSTG.startTracking();
            });

        // Hold all Amplitude track calls until identity is set, that way events are correctly
        // attached to the user identity
        this.amplitudeBuffer.install(ampli.client);

        // Manually track pageviews by watching for when the router state changes.
        // Do this manually instead of letting Angulartics manage it for us since (as of Angulartics 8.x) rewriting the url params without
        // changing the path causes a pageview event, which isn't desired since we rewrite the url params frequently
        // as part of all that reduxy stuff we do.
        let prevPath: string = null;

        this.pageTrack$
            .pipe(
                debounceTime(500), // wait for the URL query params to be set.
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                const currPath = removeOrgUidFromPath(this.window.location.pathname);
                let urlParams = this.window.location.search;
                if (urlParams?.startsWith('?')) {
                    // Trim the trailing ?
                    urlParams = urlParams.substring(1);
                }
                // Only log event if page view changed and userid is recognized.
                // Hitting the login page while not logged in would create an unidentified "unique" user which we do
                // not want to track. Once a user logs in, their device id will been associated with a user id and
                // amplitude will use that user id when it sees that device in the future.
                if (currPath && prevPath !== currPath && ampli.client.getUserId()) {
                    prevPath = currPath;
                    this.angulartics2AmplitudePSTG.pageTrack(currPath, urlParams);
                }
            });

        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(e => this.pageTrack$.next());

        // Update Amplitude with the user's domain if they are logged in
        this.authenticationService
            .isLoggedIn()
            .pipe(
                filter(Boolean),
                switchMap(() => this.effectiveOrgService.effectiveOrg$),
                filter((org): org is EffectiveOrg => org !== null),
                switchMap(effectiveOrg =>
                    forkJoin({
                        currentUser: this.cachedCurrentUserService.get().pipe(take(1)),
                        userRoles: this.applicationAssignmentsService
                            .getUserRoleNamesForEffectiveOrg(effectiveOrg.uid)
                            .pipe(take(1)),
                    }).pipe(map(rest => ({ ...rest, effectiveOrg }))),
                ),
                take(1),
            )
            .subscribe(({ currentUser, userRoles, effectiveOrg }) => {
                // Assign the Amplitude user id with the user original email, which is a good username to use and doesn't change when impersonating. Progress!
                const userEmailDomain = getEmailDomain(currentUser.email);

                const origEmail = currentUser.originalUserEmail;

                if (currentUser.impersonating) {
                    // Impersonating
                    const origEmailDomain = getEmailDomain(origEmail) || 'Unknown';
                    this.userIdentified$.next({
                        originalEmailDomain: origEmailDomain,
                        impersonatedEmailDomain: userEmailDomain,
                        originalContactId: currentUser.originalContactId,
                        impersonatedContactId: currentUser.id,
                    });
                } else {
                    // Not impersonating
                    this.userIdentified$.next({
                        originalEmailDomain: userEmailDomain,
                        impersonatedEmailDomain: null,
                        originalContactId: currentUser.id,
                        impersonatedContactId: null,
                    });
                }

                // Amplitude user properties.
                // Single source of truth for the identify email: prefer originalUserEmail
                // (stable across impersonation), fall back to email so a missing
                // originalUserEmail can't produce `(none)` domain/email.
                const identifyEmail = currentUser.originalUserEmail || currentUser.email;
                const origEmailDomain = getEmailDomain(identifyEmail);
                const ampliIdentify: IdentifyProperties = {
                    'impersonating user': currentUser.impersonating ? currentUser.email : undefined,
                    'is impersonating user': Boolean(currentUser.impersonating),
                    'pure1 org id': String(effectiveOrg?.id),
                    'pure1 org': effectiveOrg?.name,
                    'pure1 opcomp id': currentUser.operationalCompanyId,
                    'pure1 user roles': userRoles,
                    'user domain': origEmailDomain ? '@' + origEmailDomain : '',
                    'user email': identifyEmail,
                };
                this.ngZone.runOutsideAngular(() => ampli.identify(identifyEmail, ampliIdentify));

                // Identity is now set so flush any buffered events
                this.amplitudeBuffer.flush(identifyEmail);
            });
    }

    /**
     * Wrapper for init(), mostly so we can spy/stub init() in unit tests
     */
    initAmplitude(isProd: boolean): Promise<void> {
        const apiKey = isProd ? ApiKey.production : ApiKey.development;
        migrateLegacyAmplitudeCookies(this.window.document, this.window.localStorage, apiKey);
        return ampli
            .load({
                environment: isProd ? 'production' : 'development',
                client: {
                    apiKey,
                    configuration: {
                        // Amplitude team told us to never use batching. I still don't understand why - it seems like it'd just
                        // batch events phoning home, reducing http calls and overhead on the client. But Amplitude team was
                        // insistent, so... oh well. It is only a small optimization on the client side, and shouldn't
                        // affect our API calls since browsers parallelize http requests separately by target domain.
                        useBatch: false,
                        // Browser SDK 2 turns most of these on by default; SDK 1 only ever ran web
                        // attribution. Spell every one of them out so the upgrade changes no events:
                        // switching them on would flood the tracking plan with implicit events, and
                        // pageViews in particular would double-count against the manual pageview
                        // tracking in run(). Session Start/End events stay off too - Session Replay
                        // keys off the SDK's session id, which exists either way.
                        autocapture: {
                            attribution: true,
                            elementInteractions: false,
                            fileDownloads: false,
                            formInteractions: false,
                            frustrationInteractions: false,
                            networkTracking: false,
                            pageUrlEnrichment: false,
                            pageViews: false,
                            performanceTracking: false,
                            sessions: false,
                            webVitals: false,
                        },
                        // The SDK's default is a cookie on .purestorage.com, which every request to
                        // every purestorage.com host carries and which helps push the Cookie
                        // header past Kong's limit (CLOUD-278099). migrateLegacyAmplitudeCookies()
                        // above moves the existing cookies into localStorage first, so users keep
                        // their device id, session and attribution.
                        identityStorage: 'localStorage',
                    },
                },
            })
            .promise.then(() => this.amplitudeSessionReplay.install(ampli.client, isProd));
    }
}
